<template>
  <loading-content :loading="loading">
    <!-- Süsteem -> Seaded -> Uued vaated -> Kliendid uues kujunduses: the
         object page's look, with the name as a heading, the client's standing
         as pills and how many rows each tab holds. -->
    <div v-if="plate" class="obj-plate">
      <template v-if="!loading">
        <div class="obj-head">
          <div class="obj-head__text">
            <span class="obj-kicker obj-mid">KLIENDID › {{ isCompany ? 'FIRMA' : 'ERAKLIENT' }}</span>
            <div class="obj-title">
              <h1 class="obj-wide">{{ client.fullName }}</h1>
              <span v-if="city" class="obj-place">{{ city }}</span>
            </div>
            <div v-if="overview" class="obj-pills">
              <span class="obj-pill obj-pill--plain">{{ counts.objects }} {{ counts.objects === 1 ? 'objekt' : 'objekti' }}</span>
              <router-link v-if="unpaid" :to="tabLink('calendar')" class="obj-pill obj-pill--late"><i />Maksmata · {{ unpaid }}</router-link>
              <span v-else-if="counts.events" class="obj-pill obj-pill--ok"><i />Kõik töökäsud makstud</span>
              <span v-if="overview.events.last" class="obj-pill obj-pill--info">Viimati {{ day(overview.events.last.date) }} · {{ calendarCategoryById(overview.events.last.category).text }}</span>
              <span v-if="client.problematic" class="obj-pill obj-pill--late">Probleemne</span>
            </div>
          </div>
          <router-link :to="{ name: 'object/create', query: { clientId } }" class="obj-btn obj-btn--outline">
            <v-icon size="18">mdi-plus</v-icon>Lisa objekt
          </router-link>
          <router-link v-if="canEdit('client', client)" :to="{ name: 'client/edit', params: { clientId } }" class="obj-btn">
            <v-icon size="18">mdi-pencil-outline</v-icon>Muuda
          </router-link>
          <v-menu v-if="canDelete('client', client)" location="bottom end">
            <template #activator="{ props }">
              <button type="button" class="obj-icon-btn" aria-label="Rohkem" v-bind="props">
                <v-icon>mdi-dots-vertical</v-icon>
              </button>
            </template>
            <div class="obj-menu">
              <button type="button" class="obj-menu__danger" @click="confirmDelete = true">Kustuta klient</button>
            </div>
          </v-menu>
        </div>
        <div v-if="client.problematic && client.problematicNote" class="obj-reason-band">
          <span class="obj-cap">PROBLEEMNE KLIENT — PÕHJUS</span>
          <span>{{ client.problematicNote }}</span>
        </div>
        <nav class="obj-tabs" aria-label="Kliendi sakid">
          <router-link v-for="item in menu" :key="item.title" :to="item.link" class="obj-tab">
            {{ item.title }}<span v-if="item.count" class="obj-tab__n">{{ item.count }}</span>
          </router-link>
        </nav>
      </template>
      <router-view v-if="!loading" class="mt-4" :client="client" :clientId="clientId" plate :overview="overview" @changed="refreshOverview" />
    </div>
    <template v-else>
    <template v-if="!loading">
      <div class="d-flex flex-row justify-space-between">
        <div class="text-h4">{{client.fullName}}</div>
        <dropdown v-if="menuItems.length" title="Halda" :items="menuItems" />
      </div>
      <v-tabs class="mt-4" bg-color="blue-grey-lighten-5" show-arrows>
        <v-tab v-for="item in menu" :key="item.title" :to="item.link">{{item.title}}</v-tab>
      </v-tabs>
      <router-view class="mt-4" :client="client" :clientId="clientId" />
    </template>
    </template>
    <confirm
      @cancel="confirmDelete = null"
      @confirm="destroy()"
      :visible="!!confirmDelete"
      heading="Kustuta klient?"
      text="Klient kaob nimekirjast ja otsingust. Tema objektid ja töökäsud jäävad alles."
      action="Kustuta klient"
      :detail="client.fullName"
      :detail-sub="client.phone || client.email || ''"
    />
  </loading-content>
</template>

<script>
import api from '@/api'
import { mapState, mapActions } from 'vuex'
import dayjs from 'dayjs'
import helpersMixin from '@/plugins/helpersMixin'
import loadArchivo from '@/plugins/archivo'
import '@/styles/object-plate.css'
import '@/styles/client-plate.css'

export default {
  mixins: [ helpersMixin ],
  props: {
    id: {
      required: false
    }
  },
  data () {
    return {
      loading: false,
      client: {},
      // Counts, objects and the last töökäsk for the new look; null until read.
      overview: null,
      confirmDelete: null
    }
  },
  computed: {
    ...mapState(['features']),
    clientId () {
      return this.id || this.$route.params.clientId
    },
    plate () {
      return !!(this.features && this.features.clientPlate)
    },
    isCompany () {
      return this.client.type === 2
    },
    // fullAddress is "address, city, country"; the city goes beside the name.
    city () {
      return ((this.client.fullAddress || '').split(', ')[1] || '').trim()
    },
    counts () {
      return (this.overview && this.overview.counts) || {}
    },
    unpaid () {
      return (this.overview && this.overview.events.unpaid) || 0
    },
    menu () {
      return [{
        title: 'Info',
        link: this.tabLink('info')
      }, {
        title: 'Kalender',
        link: this.tabLink('calendar'),
        count: this.counts.events
      }, {
        title: 'Ajalugu',
        link: this.tabLink('history'),
        count: this.counts.history
      }].filter(({ hide }) => !hide)
    },
    menuItems () {
      return [{
        title: 'Muuda',
        link: { name: 'client/edit', params: { clientId: this.clientId }},
        hide: !this.canEdit('client', this.client)
      }, {
        title: 'Kustuta',
        hide: !this.canDelete('client', this.object),
        onClick: () => {
          this.confirmDelete = true
        }
      }].filter(({ hide }) => !hide)
    }
  },
  watch: {
    async clientId () {
      this.refresh()
    },
    // A tab may have changed something its count says.
    '$route.name' () {
      this.refreshOverview()
    },
    plate: {
      handler (on) {
        if (on) {
          loadArchivo()
          this.refreshOverview()
        }
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions(['showNotification']),
    async refresh () {
      if (!this.clientId) {
        return
      }
      this.loading = true
      const { client } = await api.getClient(this.clientId)
      this.client = client
      this.loading = false
      this.refreshOverview()
    },
    // Only the new look asks; a failure leaves the tabs without numbers rather
    // than the page without content.
    async refreshOverview () {
      if (!this.plate || !this.clientId) return
      const clientId = this.clientId
      try {
        const overview = await api.getClientOverview(clientId)
        if (clientId === this.clientId) this.overview = overview
      } catch (err) {
        this.overview = null
      }
    },
    tabLink (tab) {
      return { name: 'client/view/' + tab, params: { clientId: this.clientId } }
    },
    day (date) {
      return date ? dayjs(date).format('DD.MM.YYYY') : '—'
    },
    async destroy () {
      this.loading = true
      this.confirmDelete = null
      try {
        await api.deleteClient(this.clientId)
      } catch (err) {
        this.showNotification({ text: err.message || 'Kustutamine ebaõnnestus', color: 'red' })
        this.loading = false
        return
      }
      this.showNotification({ text: 'Klient kustutatud', color: 'success' })
      // Replace, not push: Back must not return to a client that is gone. The
      // page stays in its loading state until it is left, so no tab re-mounts
      // and asks for the deleted client again.
      this.$router.replace({ name: 'client/list', params: { type: 'all' }}).catch(() => {})
    }
  },
  mounted () {
    this.refresh()
  }
}
</script>